import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { escalationService } from '../services/api';
import { Card, Input, Button, NormalMultiSelect, useToast } from './common';
import Breadcrumbs from './common/Breadcrumbs';
import { usePageTitle } from '../context/PageTitleContext';
import { useAuth } from '../context/AuthContext';
import useDepartment from '../hooks/useDepartment';

const CreateEditEscalation = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const toast = useToast();
  const { setPageTitle } = usePageTitle();

  const isWholeNumber = (value) => /^\d+$/.test(String(value).trim());
  
  const { user } = useAuth();
  const dept = useDepartment();
  // Equipment is a BioMedical master; IT has no equivalent on a rule.
  const usesEquipment = (user?.department_code || 'IT') === 'BM';

  const [selectedRegions, setSelectedRegions] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedEquipment, setSelectedEquipment] = useState([]);
  const [isDefault, setIsDefault] = useState(false);
  const [levels, setLevels] = useState([createEmptyLevel(1)]);
  
  const [regions, setRegions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [users, setUsers] = useState([]);
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [regionsError, setRegionsError] = useState('');
  const [categoriesError, setCategoriesError] = useState('');
  
  const isEditMode = !!id && location.pathname.includes('/edit/');
  const isViewMode = !!id && location.pathname.includes('/view/');

  useEffect(() => {
    setPageTitle(isViewMode ? 'View Escalation Rule' : isEditMode ? 'Edit Escalation Rule' : 'Add Escalation Rule');
  }, [isEditMode, isViewMode, setPageTitle]);

  useEffect(() => {
    loadDropdownData();
  }, []);

  useEffect(() => {
    if (isEditMode || isViewMode) {
      fetchEscalationData();
    }
  }, [id, isEditMode, isViewMode]);

  function createEmptyLevel(levelNumber) {
    return {
      level_number: levelNumber,
      resolution_p1_hours: '',
      resolution_p2_hours: '',
      resolution_p3_hours: '',
      user_ids: [],
      other_contacts: [],
      errors: {}
    };
  }

  // Equipment follows the chosen categories: pick "OT Equipment" and the list
  // narrows to devices mapped to it. Anything already selected that falls
  // outside the new categories is dropped, so the payload cannot contain a
  // device the rule no longer covers.
  useEffect(() => {
    if (!usesEquipment) return;

    let cancelled = false;
    const categoryIds = selectedCategories.join(',');

    escalationService
      .getEquipmentDropdown(
        categoryIds || undefined,
        selectedEquipment.length > 0 ? selectedEquipment.join(',') : undefined
      )
      .then((res) => {
        if (cancelled) return;
        const options = res.data.map((e) => ({ value: e.value, label: e.label }));
        setEquipment(options);
        const allowed = new Set(options.map((o) => o.value));
        setSelectedEquipment((prev) => prev.filter((v) => allowed.has(v)));
      })
      .catch((err) => console.error('Failed to load equipment options:', err));

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategories, usesEquipment]);

  const loadDropdownData = async () => {
    try {
      const [regionsRes, categoriesRes, usersRes] = await Promise.all([
        escalationService.getRegionsDropdown(),
        escalationService.getCategoriesDropdown(),
        escalationService.getUsersDropdown()
      ]);
      
      setRegions(regionsRes.data.map(r => ({ value: r.value, label: r.label })));
      setCategories(categoriesRes.data.map(c => ({ value: c.value, label: c.label })));
      setUsers(usersRes.data.map(u => ({ value: u.value, label: u.label })));
    } catch (err) {
      console.error('Failed to load dropdown data:', err);
      toast.error('Failed to load form data');
    }
  };

  const fetchEscalationData = async () => {
    try {
      setLoading(true);
      const response = await escalationService.getById(id);
      const rule = response.data;
      
      const regionIds = rule.regions?.map(r => r.id) || [];
      const categoryIds = rule.categories?.map(c => c.id) || [];
      setSelectedRegions(regionIds);
      setSelectedCategories(categoryIds);
      setIsDefault(rule.is_default || false);
      
      let allUserIds = [];
      if (rule.levels && rule.levels.length > 0) {
        const loadedLevels = rule.levels.map(level => ({
          level_number: level.level_number,
          resolution_p1_hours: level.resolution_p1_hours ?? '',
          resolution_p2_hours: level.resolution_p2_hours ?? '',
          resolution_p3_hours: level.resolution_p3_hours ?? '',
          user_ids: level.users?.map(u => u.user_id) || [],
          other_contacts: level.other_contacts?.map(c => ({
            contact_name: c.contact_name,
            contact_email: c.contact_email
          })) || [],
          errors: {}
        }));
        setLevels(loadedLevels);
        allUserIds = [...new Set(loadedLevels.flatMap(l => l.user_ids))];
      }

      // Re-fetch dropdowns with currently mapped IDs included (even if deleted/inactive)
      try {
        const [regionsRes, categoriesRes, usersRes] = await Promise.all([
          escalationService.getRegionsDropdown(regionIds.length > 0 ? regionIds.join(',') : undefined),
          escalationService.getCategoriesDropdown(categoryIds.length > 0 ? categoryIds.join(',') : undefined),
          escalationService.getUsersDropdown(allUserIds.length > 0 ? allUserIds.join(',') : undefined)
        ]);
        setRegions(regionsRes.data.map(r => ({ value: r.value, label: r.label })));
        setCategories(categoriesRes.data.map(c => ({ value: c.value, label: c.label })));
        setUsers(usersRes.data.map(u => ({ value: u.value, label: u.label })));

        if (usesEquipment) {
          const equipmentIds = rule.equipment?.map(e => e.id) || [];
          setSelectedEquipment(equipmentIds);
          const eqRes = await escalationService.getEquipmentDropdown(
            categoryIds.length > 0 ? categoryIds.join(',') : undefined,
            equipmentIds.length > 0 ? equipmentIds.join(',') : undefined
          );
          setEquipment(eqRes.data.map(e => ({ value: e.value, label: e.label })));
        }
      } catch (err) {
        console.error('Failed to reload dropdown data with includes:', err);
      }
    } catch (err) {
      toast.error('Failed to fetch escalation rule details');
      console.error(err);
      navigate('/admin/escalations');
    } finally {
      setLoading(false);
    }
  };

  const handleAddLevel = () => {
    const newLevelNumber = levels.length + 1;
    if (newLevelNumber > 10) {
      toast.error('Maximum 10 levels allowed');
      return;
    }
    setLevels([...levels, createEmptyLevel(newLevelNumber)]);
  };

  const handleRemoveLevel = (index) => {
    if (levels.length === 1) {
      toast.error('At least one level is required');
      return;
    }
    const newLevels = levels.filter((_, i) => i !== index);
    // Renumber levels
    const renumberedLevels = newLevels.map((level, idx) => ({
      ...level,
      level_number: idx + 1
    }));
    setLevels(renumberedLevels);
  };

  const updateLevel = (index, field, value) => {
    const newLevels = [...levels];
    newLevels[index] = { ...newLevels[index], [field]: value };
    // Clear error for this field
    if (newLevels[index].errors[field]) {
      newLevels[index].errors = { ...newLevels[index].errors, [field]: '' };
    }
    setLevels(newLevels);
  };

  const updateWholeNumberLevel = (index, field, value) => {
    if (value === '' || isWholeNumber(value)) {
      updateLevel(index, field, value);
    }
  };

  const handleAddOtherContact = (levelIndex) => {
    const newLevels = [...levels];
    newLevels[levelIndex].other_contacts.push({ contact_name: '', contact_email: '' });
    setLevels(newLevels);
  };

  const handleRemoveOtherContact = (levelIndex, contactIndex) => {
    const newLevels = [...levels];
    newLevels[levelIndex].other_contacts = newLevels[levelIndex].other_contacts.filter((_, i) => i !== contactIndex);
    setLevels(newLevels);
  };

  const updateOtherContact = (levelIndex, contactIndex, field, value) => {
    const newLevels = [...levels];
    newLevels[levelIndex].other_contacts[contactIndex][field] = value;
    setLevels(newLevels);
  };

  const validateForm = () => {
    let isValid = true;
    setRegionsError('');
    setCategoriesError('');
    setError('');

    // Default rules don't need regions/categories
    if (!isDefault) {
      if (selectedRegions.length === 0) {
        setRegionsError('At least one region is required');
        isValid = false;
      }

      if (selectedCategories.length === 0) {
        setCategoriesError('At least one category is required');
        isValid = false;
      }
    }

    // Validate each level
    const newLevels = levels.map(level => {
      const errors = {};

      // All levels must have resolution times
      if (level.resolution_p1_hours === '') {
        errors.resolution_p1_hours = 'Required';
      } else if (!isWholeNumber(level.resolution_p1_hours)) {
        errors.resolution_p1_hours = 'Whole numbers only';
      }

      if (level.resolution_p2_hours === '') {
        errors.resolution_p2_hours = 'Required';
      } else if (!isWholeNumber(level.resolution_p2_hours)) {
        errors.resolution_p2_hours = 'Whole numbers only';
      }

      if (level.resolution_p3_hours === '') {
        errors.resolution_p3_hours = 'Required';
      } else if (!isWholeNumber(level.resolution_p3_hours)) {
        errors.resolution_p3_hours = 'Whole numbers only';
      }

      // Must have at least one user or contact
      if (level.user_ids.length === 0 && level.other_contacts.length === 0) {
        errors.escalation_targets = 'At least one user or other contact is required';
      }

      // Validate other contacts
      level.other_contacts.forEach((contact, idx) => {
        if (!contact.contact_name) {
          errors[`other_contact_name_${idx}`] = 'Name is required';
        }
        if (!contact.contact_email) {
          errors[`other_contact_email_${idx}`] = 'Email is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contact.contact_email)) {
          errors[`other_contact_email_${idx}`] = 'Invalid email format';
        }
      });

      if (Object.keys(errors).length > 0) {
        isValid = false;
      }

      return { ...level, errors };
    });

    setLevels(newLevels);
    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Please fix validation errors');
      return;
    }

    try {
      setLoading(true);
      
      const payload = {
        region_ids: isDefault ? [] : selectedRegions,
        category_ids: isDefault ? [] : selectedCategories,
        equipment_ids: isDefault || !usesEquipment ? [] : selectedEquipment,
        is_default: isDefault,
        levels: levels.map(level => ({
          level_number: level.level_number,
          resolution_p1_hours: parseInt(level.resolution_p1_hours, 10),
          resolution_p2_hours: parseInt(level.resolution_p2_hours, 10),
          resolution_p3_hours: parseInt(level.resolution_p3_hours, 10),
          user_ids: level.user_ids,
          other_contacts: level.other_contacts.filter(c => c.contact_name && c.contact_email)
        }))
      };
      
      if (isEditMode) {
        await escalationService.update(id, payload);
        toast.success('Escalation rule updated successfully');
      } else {
        await escalationService.create(payload);
        toast.success('Escalation rule created successfully');
      }
      
      navigate('/admin/escalations');
    } catch (err) {
      const errorMsg = err.response?.data?.error || `Failed to ${isEditMode ? 'update' : 'create'} escalation rule`;
      setError(errorMsg);
      toast.error(errorMsg);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/admin/escalations');
  };

  if (loading && (isEditMode || isViewMode)) {
    return (
      <div className="admin-panel">
        <Card>
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <p>Loading escalation rule details...</p>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="admin-panel">
      <Card className="w-full">
        {error && (
          <div className="p-4 mb-4 bg-danger-50 text-danger-700 rounded border-l-4 border-danger-600">
            {error}
          </div>
        )}
        <form onSubmit={handleSubmit} noValidate className="w-full">
          <Breadcrumbs
            items={[
              { label: 'Escalation Rules', to: '/admin/escalations' },
              { label: isViewMode ? 'View' : isEditMode ? 'Edit' : 'Add' }
            ]}
            className="mb-6"
          />

          {/* Default Escalation Toggle */}
          <div className="flex items-center gap-3 mb-6 p-4 bg-purple-50 border border-purple-200 rounded-lg">
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={isDefault}
                onChange={(e) => {
                  setIsDefault(e.target.checked);
                  if (e.target.checked) {
                    setSelectedRegions([]);
                    setSelectedCategories([]);
                    setRegionsError('');
                    setCategoriesError('');
                  }
                }}
                className="sr-only peer"
                disabled={loading || isViewMode}
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
            </label>
            <div>
              <span className="text-sm font-medium text-purple-800">Default Escalation Rule</span>
              <p className="text-xs text-purple-600">
                {isDefault 
                  ? 'This rule applies to all tickets that don\'t match any specific region+category rule.'
                  : 'Enable to make this the fallback rule for unmatched tickets (no regions/categories needed).'}
              </p>
            </div>
          </div>

          {/* Regions and Categories — hidden for default rules */}
          {!isDefault && (
          <div className="flex flex-col md:flex-row gap-4 mb-6">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {dept.regions} <span className="text-red-500">*</span>
              </label>
              <NormalMultiSelect
                options={regions}
                value={selectedRegions}
                onChange={(e) => {
                  setSelectedRegions(e.target.value);
                  if (regionsError) setRegionsError('');
                }}
                placeholder={`Select ${dept.regions.toLowerCase()}`}
                disabled={loading || isViewMode}
              />
              {regionsError && (
                <p className="mt-1 text-sm text-red-600">{regionsError}</p>
              )}
            </div>

            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Categories <span className="text-red-500">*</span>
              </label>
              <NormalMultiSelect
                options={categories}
                value={selectedCategories}
                onChange={(e) => {
                  setSelectedCategories(e.target.value);
                  if (categoriesError) setCategoriesError('');
                }}
                placeholder="Select categories"
                disabled={loading || isViewMode}
              />
              {categoriesError && (
                <p className="mt-1 text-sm text-red-600">{categoriesError}</p>
              )}
            </div>

            {usesEquipment && (
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Equipment
                </label>
                <NormalMultiSelect
                  options={equipment}
                  value={selectedEquipment}
                  onChange={(e) => setSelectedEquipment(e.target.value)}
                  placeholder={
                    selectedCategories.length === 0
                      ? 'Select categories first'
                      : 'All equipment in these categories'
                  }
                  disabled={loading || isViewMode || selectedCategories.length === 0}
                />
                <p className="mt-1 text-xs text-gray-500">
                  Leave empty to cover every device in the selected categories.
                </p>
              </div>
            )}
          </div>
          )}

          {/* Escalation Levels */}
          <div className="mb-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Escalation Levels</h3>
              {!isViewMode && (
                <Button
                  type="button"
                  onClick={handleAddLevel}
                  variant="secondary"
                  disabled={loading || levels.length >= 10}
                >
                  + Add Escalation
                </Button>
              )}
            </div>

            {levels.map((level, index) => (
              <div key={index} className="border border-gray-200 rounded-lg mb-4 overflow-hidden">
                {/* Level Header */}
                <div className="flex justify-between items-center px-4 py-3 bg-gray-50 border-b border-gray-200">
                  <h4 className="text-md font-semibold text-gray-800">
                    Escalation {level.level_number}
                  </h4>
                  {!isViewMode && levels.length > 1 && (
                    <button
                      type="button"
                      onClick={() => handleRemoveLevel(index)}
                      className="text-red-600 hover:text-red-800 text-sm font-medium"
                      disabled={loading}
                    >
                      Remove
                    </button>
                  )}
                </div>

                {/* Two-column layout: Resolution Times LEFT | Users & Contacts RIGHT */}
                <div className="flex flex-col lg:flex-row">
                  {/* LEFT: Resolution Times */}
                  <div className="flex-1 p-4 lg:border-r border-b lg:border-b-0 border-gray-200">
                    <h5 className="text-sm font-medium text-gray-700 mb-3">
                      Resolution Times (hours) <span className="text-red-500">*</span>
                    </h5>
                    <div className="space-y-3">
                      <Input
                        label="P1 - High Priority"
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        min="0"
                        value={level.resolution_p1_hours}
                        onChange={(e) => updateWholeNumberLevel(index, 'resolution_p1_hours', e.target.value)}
                        placeholder="e.g., 4"
                        required
                        error={level.errors.resolution_p1_hours}
                        disabled={loading || isViewMode}
                        readOnly={isViewMode}
                      />
                      <Input
                        label="P2 - Medium Priority"
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        min="0"
                        value={level.resolution_p2_hours}
                        onChange={(e) => updateWholeNumberLevel(index, 'resolution_p2_hours', e.target.value)}
                        placeholder="e.g., 8"
                        required
                        error={level.errors.resolution_p2_hours}
                        disabled={loading || isViewMode}
                        readOnly={isViewMode}
                      />
                      <Input
                        label="P3 - Low Priority"
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        min="0"
                        value={level.resolution_p3_hours}
                        onChange={(e) => updateWholeNumberLevel(index, 'resolution_p3_hours', e.target.value)}
                        placeholder="e.g., 24"
                        required
                        error={level.errors.resolution_p3_hours}
                        disabled={loading || isViewMode}
                        readOnly={isViewMode}
                      />
                    </div>
                  </div>

                  {/* RIGHT: Users & Contacts */}
                  <div className="flex-1 p-4">
                    {/* Escalate To Users */}
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Escalate To Users
                      </label>
                      <NormalMultiSelect
                        options={users}
                        value={level.user_ids}
                        onChange={(e) => updateLevel(index, 'user_ids', e.target.value)}
                        placeholder="Select users"
                        disabled={loading || isViewMode}
                      />
                    </div>

                    {/* Other Contacts */}
                    <div>
                      <div className="flex justify-between items-center mb-2">
                        <label className="block text-sm font-medium text-gray-700">
                          Other Contacts
                        </label>
                        {!isViewMode && (
                          <button
                            type="button"
                            onClick={() => handleAddOtherContact(index)}
                            className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                            disabled={loading}
                          >
                            + Add Contact
                          </button>
                        )}
                      </div>

                      {level.other_contacts.map((contact, contactIndex) => (
                        <div key={contactIndex} className="flex gap-2 mb-2">
                          <div className="flex-1">
                            <Input
                              placeholder="Contact Name"
                              value={contact.contact_name}
                              onChange={(e) => updateOtherContact(index, contactIndex, 'contact_name', e.target.value)}
                              error={level.errors[`other_contact_name_${contactIndex}`]}
                              disabled={loading || isViewMode}
                              readOnly={isViewMode}
                            />
                          </div>
                          <div className="flex-1">
                            <Input
                              placeholder="Contact Email"
                              type="email"
                              value={contact.contact_email}
                              onChange={(e) => updateOtherContact(index, contactIndex, 'contact_email', e.target.value)}
                              error={level.errors[`other_contact_email_${contactIndex}`]}
                              disabled={loading || isViewMode}
                              readOnly={isViewMode}
                            />
                          </div>
                          {!isViewMode && (
                            <button
                              type="button"
                              onClick={() => handleRemoveOtherContact(index, contactIndex)}
                              className="text-red-600 hover:text-red-800 px-1"
                              disabled={loading}
                            >
                              ×
                            </button>
                          )}
                        </div>
                      ))}

                      {level.errors.escalation_targets && (
                        <p className="mt-1 text-sm text-red-600">{level.errors.escalation_targets}</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Action Buttons */}
          {!isViewMode && (
            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button
                type="button"
                onClick={handleCancel}
                variant="secondary"
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                disabled={loading}
              >
                {loading ? 'Saving...' : isEditMode ? 'Update' : 'Create'}
              </Button>
            </div>
          )}

          {isViewMode && (
            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button
                type="button"
                onClick={handleCancel}
                variant="secondary"
              >
                Back
              </Button>
            </div>
          )}
        </form>
      </Card>
    </div>
  );
};

export default CreateEditEscalation;
